import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { take, map } from 'rxjs/operators';
import { fromConsultant } from '../reducers/consultant.reducer';
import { Candidate } from '../models/candidate.model';
import { Jobtype } from '../models/jobtype.model';
import { Test } from '../models/test.model';
import { Invitation } from '../models/invitation.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppConfig } from '../helpers/app.config';
import jwtDecode from 'jwt-decode';
import * as FileSaver from 'file-saver';
import { fromQuestion } from '../reducers/question.reducer';
import { AppVersionModel } from '../models/app-config';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {

  jobtypes$: Observable<Jobtype[]>;

  feature: string;

  public ADNTestModelIds = [1, 2];

  constructor(
    private store: Store,
    private translateService: TranslateService,
    private toastr: ToastrService,
    private router: Router,
    private location: Location,
    private httpClient: HttpClient
  ) { }

  formatDate(date: Date): string {
    return date.getDate().toString().padStart(2, '0') + '/' + (date.getMonth() + 1).toString().padStart(2, '0') + '/' + date.getFullYear();
  }

  initials(candidate: Candidate): string {
    // corregido para ESFBITE-1342 por pentest para evitar XSS
    const name = (candidate.name || '').replace(/[<>"'&]/g, '');
    const surname = (candidate.surnameComplete || '').replace(/[<>"'&]/g, '');
    return (name + ' ' + surname).trim()
      .split(/\s/).reduce((a, b) => a += b.slice(0, 1).toUpperCase() + '.', '');
  }

  jobtypeFromJobtypeId(jobtypeId: number): Jobtype {
    // TODO: no sacarlo del localStorage sino del Store
    const jobtypes: Jobtype[] = JSON.parse(localStorage.getItem('jobtypes'));
    return jobtypes.find(jobtype => jobtype.id === jobtypeId);
  }

  secondsToTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return (m > 0 ? (m + 'm ') : '') + s + 's';
  }

  toast(type: string, key: string, useTranslation = true, literal?) {
    if (useTranslation) {
      this.translateService.get('TOAST.' + type.toUpperCase() + '.' + key.toUpperCase(), { x: literal }).subscribe((text: string) => {
        this.toastr[type](text, null, { enableHtml: true });
      });
    } else {
      this.toastr[type](key);
    }
  }

  customeToast(type: string, key: string, message: string, useTranslation = true) {
    if (useTranslation) {
      this.translateService.get('TOAST.' + type.toUpperCase() + '.' + key.toUpperCase()).subscribe((text: string) => {
        this.toastr[type](text + ' ' + message);
      });
    } else {
      this.toastr[type](key);
    }
  }
  public getLanguageIdFromLanguage(language) {
    switch (language) {
      case 'es-ES':
        return 1;

      case 'es-AR':
        return 2;

      case 'es-MX':
        return 3;

      case 'es-CL':
        return 4;

      case 'pt-PT':
        return 5;

      case 'pt-BR':
        return 6;

      case 'en-GB':
        return 7;

      case 'es-UY':
        return 8;

      default:
        return 1;
    }
  }

  public getLanguageFromLanguageId(languageId) {
    switch (languageId) {
      case 1:
      case 96:
        return 'es-ES';

      case 2:
        return 'es-AR';

      case 3:
        return 'es-MX';

      case 4:
        return 'es-CL';

      case 5:
        return 'pt-PT';

      case 6:
        return 'pt-BR';

      case 7:
        return 'en-GB';

      case 8:
        return 'es-UY';

      default:
        return 'es-ES';
    }
  }

  public capitalizeFirstLetter(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  public titlecase(text: string) {
    return text?.length ? text.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : '';
  }

  public setLastUrl() {
    localStorage.setItem('lastUrl', this.router.url);
  }

  public goBack() {
    this.location.back();
  }

  public getTestName(test: Test, invitation: Invitation): string {
    if ((test.testModel.origin === 'ct' && this.ADNTestModelIds.includes(test.testModelId))) {
      return test.testModel?.name.toLocaleLowerCase()
        .replace('normativo', 'NR').replace('ipsativo', 'IP').toUpperCase() + '-' +
        this.titlecase(this.translateService.instant('JOBTYPES.' + invitation.jobtype.randstadJobtype))
      // this.titlecase(invitation.jobtype?.name);
    } else {
      return this.titlecase(test.testModel?.name);
    }
  }

  public changeLanguage(language: string, candidateId?: number): Observable<string> {
    this.translateService.use(language);
    document.documentElement.lang = language.split('-')[0];

    // Si viene un candidato le cambiamos su idioma en BBDD
    if (candidateId && this.feature !== 'error') {
      const languageId = this.getLanguageIdFromLanguage(language);
      // convertido en promesa para que no sea necesario subscribirse cada vez para que se ejecute la llamada
      return this.httpClient.put<string>(AppConfig.settings.API + '/candidates/language', { languageId, candidateId });
    } else {
      return of(language);
    }
  }

  getClientProjects(officeId: number): Observable<any> {
    return this.httpClient.get<any>(AppConfig.settings.API + '/projects',
      { params: { officeId } as any }
    );
  }

  // Genera el enlace de la home del candidato, pasando un token
  getHomeLink(token: string) {
    let url = '';
    url = AppConfig.settings.HOME + '/#/candidate/home/';
    url += token;
    return url;

  }

  public exportToCsv(rows: object[], fileName: string, columns?: string[]): string {
    if (!rows || !rows.length) {
      return;
    }
    const separator = ';';

    const csvContent = columns.join(separator) + '\n' + rows.map(row => {
      return columns.map(k => {
        let cell = row[k] === null || row[k] === undefined ? '' : row[k];
        cell = cell instanceof Date
          ? cell.toLocaleString()
          : cell.toString().replace(/"/g, '""');
        if (cell.search(/("|,|\n)/g) >= 0) {
          cell = `"${cell}"`;
        }
        return cell;
      }).join(separator);
    }).join('\n');

    this.saveAsFile(csvContent, fileName);
  }

  private saveAsFile(buffer: string, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(data, fileName + '.csv');
  }

  // Genera un ojjeto de HttpParams sin los valores a null/undefined
  public createHttpParams(params: {}): HttpParams {
    let httpParams: HttpParams = new HttpParams();
    Object.keys(params).forEach(param => {
      if (params[param]) {
        httpParams = httpParams.set(param, params[param]);
      }
    });

    return httpParams;
  }

  checkFeature() {
    // comprobamos si esta en la home en las instrucciones o en un test;
    if (this.router.url.includes('/home/')) {
      document.getElementById('candidateModule').style.backgroundColor = 'white';
      this.feature = 'home';

    } else if (this.router.url.includes('/test/')) {
      this.store.pipe(select(fromQuestion.selectQuestionState)).subscribe(question => {
        if (question?.testModelId === 1 || question?.testModelId === 2) {
          document.getElementById('candidateModule').style.backgroundColor = 'white';

          if (!question?.questions?.length && !question?.levelCompleted && question?.testStatus !== 'completed' && question?.testStatus !== 'expired') {
            this.feature = 'adn-instruction';
          } else {
            this.feature = 'adn-test';
          }
        }
        else if (question?.testModelId === 24) {
          document.getElementById('candidateModule').style.backgroundColor = 'white';

          if (!question?.questions?.length && !question?.levelCompleted && question?.testStatus !== 'completed' && question?.testStatus !== 'expired') {
            this.feature = 'english-instruction';
          } else {
            this.feature = 'english-test';
          }
        }
        else if (question?.testModelId === 25) {
          document.getElementById('candidateModule').style.backgroundColor = 'white';

          if (!question?.questions?.length && !question?.levelCompleted && question?.testStatus !== 'completed' && question?.testStatus !== 'expired' && question.levels?.length) {
            this.feature = 'reasoning-instruction';
          } else {
            this.feature = 'reasoning-test';
          }
        }
        else if (question?.testModelId === 26) {
          // seteamos los colores de fondo de la prueba
          document.getElementById('candidateModule').style.backgroundColor = 'white';

          if (!question?.questions?.length && !question?.levelCompleted && question?.testStatus !== 'completed' && question?.testStatus !== 'expired') {
            this.feature = 'digital-instruction';
          } else {
            this.feature = 'digital-test';

            if (this.feature === 'digital-instruction' || (this.feature === 'digital-test' && question.questions?.length)) {
              // Deshabilitar el spinner por las proximas 2 llamadas (putAnswer y getNextQuestion)
              localStorage.setItem('spinnerDisabled', '2');
            } else {
              localStorage.setItem('spinnerDisabled', '0');
            }
          }
        }
      });
    } else if (this.router.url.includes('/error')) {
      document.getElementById('errorModule').style.backgroundColor = 'white';
      this.feature = 'error';
    } else if (this.router.url.includes('/terms')) {
      document.getElementById('candidateModule').style.backgroundColor = 'white';
      this.feature = 'legal';
    }
  }

  public getLanguagesNotSelected(clientId: number) {
    const languageSelected = this.translateService.currentLang;
    const languagesAvailable = ['en-GB', 'pt-PT'];

    switch (clientId) {
      case 1:
        languagesAvailable.push('es-ES')
        break;

      case 2:
        languagesAvailable.push('es-AR')
        break;

      case 3:
        languagesAvailable.push('es-MX')
        break;

      case 4:
        languagesAvailable.push('es-CL')
        break;

      case 5:
        languagesAvailable.push('pt-PT')
        break;

      case 6:
        languagesAvailable.push('pt-BR')
        break;

      case 7:
        languagesAvailable.push('en-GB')
        break;
    }
    const languagesNotSelected = [];

    languagesAvailable.map(lang => {
      if (lang !== languageSelected) {
        languagesNotSelected.push(lang);
      }
    });
    return languagesNotSelected;
  }

  // Divide el array en gurpos
  arrayInGroups(arr, size) {
    const chunk = []; // declara array vacio
    for (let i = 0; i < arr.length; i += size) { // loop que recorre el array
      // Buscamos el limite del array
      const limit = i + size >= arr.length ? arr.length : i + size;
      chunk.push(arr.slice(i, limit));
    } // push al array el tramo desde el indice del loop hasta el valor size + el indicador
    return chunk;
  }

  // Decodificamos base64 para los acentos y tildes
  // Después de utilizar   DOMParser().parseFromString('cadena', 'text/html')
  // Al inyectar como código HTML con alguna propiedad, pierde el UTF-8, esta función solventa eso.
  b64DecodeUnicode(str) {
    return decodeURIComponent(Array.prototype.map.call(atob(str), (c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }

  reportFileName(test: Test, language: string, reportType: string, isMD = false): string {
    const date = new Date().toJSON().split('T')[0];
    let fileName = '';

    language = language.split('-')[0].toUpperCase();
    fileName = test.invitationId ? test.invitationId.toString() : 'ADN-' + test.externalId.toString();

    const fullName = test.candidate?.name ? this.titlecase(test.candidate.name) : test.candidate.randstadCandidate;
    const initialsName = test.candidate?.name ? this.initials(test.candidate) : test.candidate.randstadCandidate;

    switch (reportType) {
      case 'candidate':
        fileName += '_'
          + this.translateService.instant('REPORT.FILENAME.CANDIDATE', { testModelName: this.translateService.instant('REPORT.FILENAME.TESTMODELNAME.' + (isMD ? 'MD' : test.testModelId)) }) + ', '
          + fullName + '_'
          + date + '.pdf';
        break;

      case 'consultant':
      case 'client':
        fileName += '_' + initialsName.replace(/\./g, '') + '_';
        if (this.ADNTestModelIds.includes(test.testModelId)) {
          fileName += (isMD
            ? this.titlecase(this.translateService.instant('TESTMODELS.DIGITAL')).replace(' ', '_') + '_'
            : (test.adjustment ? test.idealProfileName + '_' : '') + (test.testModelId === 1 ? 'ADN_NR' : 'ADN_IP') + '_')
        } else {
          fileName += this.titlecase(this.translateService.instant('TESTMODELS.' + test.testModel.origin.toLocaleUpperCase() + '.TYPE.' + test.testModel.type)).replace(' ', '_') + '_';
        }
        fileName += (language.length ? language + '_' : '')
          + this.translateService.instant('REPORTS.REPORTTYPES.' + reportType.toUpperCase()) + '_'
          + date + '.pdf';
        break;
    }

    return fileName;
  }

  cleanParams(originalParams) {
    const cleanParams: any = {};
    for (const key in originalParams) {
      if (originalParams[key] && key !== 'type') {
        cleanParams[key] = originalParams[key];
      }
    }

    if (originalParams.status) {
      cleanParams.status = originalParams.status;
    }

    cleanParams.adnSearch = 1;

    // Agregamos origin para la búsqueda de pruebas.
    cleanParams.origin = 'ct';

    return cleanParams;
  }

  // Devolvemos la versión del proyecto
  // app/version
  getAppVersion() {
    const version = new AppVersionModel();
    version.appName = 'CTFRONT';
    version.version = '1.0'; // Ya no tenemos versiones de octopus, hay que hacer algo nuevo

    // version.version = `${AppConfig.settings.VERSION}`;
    return version;
  }

  getNacsData(request = null) {
    const NacsData = {
      currentUrl: '',
      localStorage: {
        lastUrl: '',
        consultant: {
          // googleDirectory: '',
          office: '',
          // permissions: '',
          // raas: '',
          token: ''
        }
      },
      request: request
    };

    const currentUrl = location.href;
    NacsData.currentUrl = currentUrl;

    const consultant = JSON.parse(localStorage.getItem('consultant'));

    NacsData.localStorage = {
      lastUrl: localStorage.getItem('lastUrl'),
      consultant: {
        // googleDirectory: consultant.googleDirectory,
        office: consultant.office,
        // permissions: consultant.permissions,
        // raas: consultant.raas,
        token: consultant.token
      }
    }
    return btoa(JSON.stringify(NacsData));
  }

  public roundUp(value: number) {
    return Math.ceil(value);
  }

}
